import React, { useEffect, useRef, useState } from 'react'
import { useStore } from '../../store/store'
import { App, Collapse, InputNumber } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { PRIMARY_DARK } from '../../constants/constants'
import { integerInputProps } from '../../utils/inputs'

// Grille de référence : les largeurs sont saisies en douzièmes, comme dans
// Pluck. Le modèle stocke des fractions (column.size), et tous les presets
// existants tombent juste sur des douzièmes (1/2 = 6/12, 1/3 = 4/12,
// 1/4 = 3/12) — la conversion est donc sans perte.
const GRID = 12

// Plafond aligné sur le reste de l'éditeur : BorderColumn refuse d'ajouter
// au-delà de 4 colonnes, et columnDefaultDisposition ne va pas plus loin.
const MAX_COLUMNS = 4
const MAX_ROWS = 10

const toTwelfths = (size) => Math.min(GRID, Math.max(1, Math.round(size * GRID)))
const toSizes = (twelfths) => twelfths.map((t) => t / GRID)

// Répartition équitable de `total` douzièmes, le reliquat allant aux premières
// colonnes. `total` vaut 12 pour la page, la largeur de la colonne parente pour
// les sous-colonnes d'une ligne.
const evenTwelfths = (count, total = GRID) =>
  Array.from(
    { length: count },
    (_, idx) => Math.floor(total / count) + (idx < total % count ? 1 : 0)
  )

// La dernière colonne absorbe ce qui reste : c'est la seule façon de garantir
// que la somme fasse toujours `total` sans imposer de saisie cohérente à l'oeil.
const withWidth = (twelfths, index, value, total = GRID) => {
  const next = [...twelfths]
  next[index] = value
  const headSum = next.slice(0, -1).reduce((sum, t) => sum + t, 0)
  next[next.length - 1] = total - headSum
  return next
}

// Maximum saisissable pour une colonne : la dernière doit garder au moins
// `lastMin` douzièmes (1, ou ce qu'exigent ses sous-colonnes, cf. minWidthOf).
const maxWidthFor = (twelfths, index, total = GRID, lastMin = 1) => {
  const otherHeadSum = twelfths
    .slice(0, -1)
    .reduce((sum, t, idx) => (idx === index ? sum : sum + t), 0)
  return total - otherHeadSum - lastMin
}

// Sous-colonnes d'une ligne en douzièmes de PAGE : le modèle stocke leur taille
// en fraction de la colonne parente, qui fait elle-même `parentTwelfths`/12.
// Aucune ne descend sous 1/12 de page, la dernière prend le reste.
const rowPageTwelfths = (columns, parentTwelfths) => {
  const heads = columns
    .slice(0, -1)
    .map((c) => Math.max(1, Math.round(c.size * parentTwelfths)))
  const last = parentTwelfths - heads.reduce((sum, t) => sum + t, 0)
  return last >= 1 ? [...heads, last] : evenTwelfths(columns.length, parentTwelfths)
}

// Largeur minimale d'une colonne découpée : chaque sous-colonne doit garder
// 1/12 de page. Elles sont stockées en fraction de la parente, donc la plus
// étroite (fraction f) impose une parente d'au moins 1/f douzièmes — le seul
// nombre de sous-colonnes ne suffit pas (5 + 1 dans 6/12 ne descend pas à 2/12).
// Tolérance : les tailles héritées arrondies (0.33) ne doivent pas gonfler 3 en 4.
const minWidthOf = (columnInfos) =>
  Math.max(
    1,
    ...columnInfos.rows.flatMap((row) =>
      row.columns.map((sub) => Math.ceil(1 / sub.size - 0.05))
    )
  )

// Ce qu'on venait lire dans le bloc : sa largeur et son découpage. Affiché dans
// l'en-tête pour qu'une colonne repliée reste identifiable sans la déplier.
const sumBoxCount = (items) => items.reduce((total, item) => total + item.boxCount, 0)

// Boxes perdues en ramenant une colonne à `rowCount` lignes : réduire supprime
// les dernières lignes, revenir à une colonne simple ne garde qu'une box.
const boxesLostByRowCount = (columnInfos, rowCount) => {
  if (!columnInfos.rows.length) return 0
  if (rowCount <= 1) return Math.max(sumBoxCount(columnInfos.rows) - 1, 0)
  return sumBoxCount(columnInfos.rows.slice(rowCount))
}

const summarize = (columnInfos, twelfths) => {
  const width = `${twelfths}/${GRID}`
  if (columnInfos.drawMode) return `${width} · dessin`
  const rowCount = columnInfos.rows.length
  return rowCount > 1 ? `${width} · ${rowCount} lignes` : width
}

const labelStyle = { fontWeight: 700, color: PRIMARY_DARK, fontSize: 11 }
const mutedLabelStyle = { color: PRIMARY_DARK, fontSize: 11, opacity: 0.65 }
const fieldStyle = { width: '100%' }
const groupStyle = {
  borderLeft: `2px solid rgba(11, 34, 57, 0.15)`,
  paddingLeft: 8,
  marginLeft: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  // Repère de la cellule cliquée, posé en absolu par-dessus le bloc.
  position: 'relative',
}

const WidthFields = ({ twelfths, total, onChange }) => (
  <>
    {twelfths.map((value, idx) => {
      const isLast = idx === twelfths.length - 1
      return (
        <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={isLast ? mutedLabelStyle : labelStyle}>
            Col. {idx + 1}{isLast ? ' (reste)' : ''}
          </span>
          <InputNumber
            {...integerInputProps}
            size='small'
            style={fieldStyle}
            disabled={isLast || undefined}
            min={1}
            // Pas de plafond sur la colonne « reste » : calculé pour elle, il
            // valait sa largeur moins 1, et antd affichait le champ en rouge.
            max={isLast ? undefined : maxWidthFor(twelfths, idx, total)}
            value={value}
            suffix={`/ ${GRID}`}
            onChange={(v) => v && onChange(idx, v)}
          />
        </div>
      )
    })}
  </>
)

/**
 * Réglages avancés de la grille du container, calqués sur Pluck : nombre de
 * colonnes principales, largeur de chacune en douzièmes, nombre de lignes, et
 * pour chaque ligne le nombre et la largeur de ses sous-colonnes.
 */
const AdvancedLayoutSettings = ({
  containerId,
  columnsInfos,
  applyContainerDisposition,
  setColumnRowCount,
  splitColumnIntoSubColumns,
  focusedColumnId,
  focusedCellId,
  focusNonce,
  canSplit = true,
}) => {
  const { modal: modalApi } = App.useApp()
  const columnTwelfths = columnsInfos.map((c) => toTwelfths(c.size))
  const [openColumnIds, setOpenColumnIds] = useState([])
  const panelBodies = useRef(new Map())
  const focusLayoutColumn = useStore(({ focusLayoutColumn }) => focusLayoutColumn)

  // Cellule cliquée sur le canvas : on déplie sa colonne et on l'amène sous les
  // yeux ; elle reste éclairée tant que la cellule est désignée (cf.
  // ContainerLayoutPanel, qui l'éteint à sa fermeture). Le défilement attend la fin de
  // l'animation de dépliage (celle-ci et celle de « Réglages avancés » au-dessus,
  // jouées ensemble) : viser avant que les hauteurs soient stabilisées mène à côté.
  useEffect(() => {
    if (!focusedColumnId) return
    setOpenColumnIds((ids) => (ids.includes(focusedColumnId) ? ids : [...ids, focusedColumnId]))
    const scrollTimer = setTimeout(() => {
      const body = panelBodies.current.get(focusedColumnId)
      const panel = body?.closest('.ant-collapse-item') ?? body
      // 'nearest' : on remonte le panel de droite juste ce qu'il faut, sans
      // emporter le canvas au passage.
      panel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 320)
    return () => clearTimeout(scrollTimer)
  }, [focusedColumnId, focusNonce])

  // Voile posé PAR-DESSUS le bloc plutôt qu'un fond sur le bloc lui-même.
  const focusOverlay = (targetId) =>
    focusedCellId === targetId ? (
      <div className='layout-focus-highlight' aria-hidden='true' />
    ) : null

  // Régler une autre colonne la désigne : son bloc et sa cellule dans le
  // document s'éclairent à la place. Une cellule déjà désignée dans cette
  // colonne (ligne cliquée sur le canvas) garde son repère plus précis.
  const focusColumn = (columnId) => {
    if (focusedColumnId !== columnId) focusLayoutColumn(columnId)
  }

  // Retirer une colonne, une ligne ou une sous-colonne emporte son contenu :
  // on confirme dès qu'une box y passerait. Annuler ne touche pas au store, le
  // champ (contrôlé) revient donc de lui-même à la valeur en place.
  //
  // Une seule confirmation à la fois : la modale prend le focus, le champ perd le
  // sien, et rc-input-number réémet alors onChange au blur (la valeur contrôlée
  // n'a pas bougé, puisqu'on attend la réponse). Sans ce garde, une frappe
  // ouvrait deux modales empilées, et annuler la première laissait la seconde.
  const confirmOpen = useRef(false)
  const confirmIfBoxesLost = (lostBoxCount, apply) => {
    if (lostBoxCount <= 0) {
      apply()
      return
    }
    if (confirmOpen.current) return
    confirmOpen.current = true
    const release = () => {
      confirmOpen.current = false
    }
    modalApi.confirm({
      title: 'Attention',
      // Sinon la barre flottante du container passe par-dessus (cf. index.less)
      rootClassName: 'container-layout-modal-root',
      icon: <ExclamationCircleFilled />,
      content:
        'Cette action supprime aussi des éléments de ce bloc. Voulez-vous continuer ?',
      okText: 'Continuer',
      cancelText: 'Annuler',
      onOk: () => {
        release()
        apply()
      },
      onCancel: release,
      afterClose: release,
    })
  }

  const setColumnCount = (count) =>
    confirmIfBoxesLost(
      columnsInfos
        .slice(count)
        .reduce((total, c) => total + c.totalBoxCount, 0),
      () =>
        applyContainerDisposition({
          containerId,
          columnsSizes: toSizes(evenTwelfths(count)),
        })
    )

  const setRowCount = (columnInfos, rowCount) =>
    confirmIfBoxesLost(boxesLostByRowCount(columnInfos, rowCount), () =>
      setColumnRowCount({ columnId: columnInfos.id, rowCount })
    )

  const setColumnWidth = (index, value) =>
    applyContainerDisposition({
      containerId,
      columnsSizes: toSizes(withWidth(columnTwelfths, index, value)),
    })

  // Lignes : saisie en douzièmes de page, stockage en fraction de la parente.
  const setRowColumnCount = (row, count, parentTwelfths) =>
    confirmIfBoxesLost(sumBoxCount(row.columns.slice(count)), () =>
      applyContainerDisposition({
        containerId: row.id,
        columnsSizes: evenTwelfths(count, parentTwelfths).map((t) => t / parentTwelfths),
      })
    )

  const setRowColumnWidth = (rowId, currentTwelfths, index, value, parentTwelfths) =>
    applyContainerDisposition({
      containerId: rowId,
      columnsSizes: withWidth(currentTwelfths, index, value, parentTwelfths).map(
        (t) => t / parentTwelfths
      ),
    })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={labelStyle}>Colonnes principales</span>
        <InputNumber
          {...integerInputProps}
          size='small'
          style={fieldStyle}
          min={1}
          max={MAX_COLUMNS}
          value={columnsInfos.length}
          onChange={(v) => v && setColumnCount(v)}
        />
      </div>

      {/* Une colonne repliée n'affiche que son résumé : sans ça, quatre colonnes
          découpées en lignes donnaient une pile de champs plus haute que le
          panel, où retrouver un réglage demandait de tout parcourir. Repliées
          par défaut, l'en-tête portant l'information qu'on venait y lire. */}
      <Collapse
        size='small'
        ghost
        className='advanced-columns-collapse'
        activeKey={openColumnIds}
        onChange={(keys) => {
          // Déplier une colonne la désigne, comme la régler.
          const opened = keys.filter((key) => !openColumnIds.includes(key))
          setOpenColumnIds(keys)
          if (opened.length) focusColumn(opened[opened.length - 1])
        }}
        items={columnsInfos.map((columnInfos, columnIdx) => ({
          key: columnInfos.id,
          label: (
            <span style={mutedLabelStyle}>
              Colonne principale {columnIdx + 1}
              <span style={{ opacity: 0.8 }}> — {summarize(columnInfos, columnTwelfths[columnIdx])}</span>
            </span>
          ),
          children: (
            <div
              ref={(node) => {
                if (node) panelBodies.current.set(columnInfos.id, node)
                else panelBodies.current.delete(columnInfos.id)
              }}
              onClick={() => focusColumn(columnInfos.id)}
              style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}
            >
              {/* Colonne simple : c'est la colonne elle-même qui a été cliquée,
                  le repère porte donc sur tout son bloc. Découpée en lignes,
                  c'est la ligne concernée qui s'éclaire, plus bas. */}
              {focusOverlay(columnInfos.id)}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={columnIdx === columnsInfos.length - 1 ? mutedLabelStyle : labelStyle}>
                  Largeur{columnIdx === columnsInfos.length - 1 ? ' (reste)' : ''}
                </span>
                <InputNumber
                  {...integerInputProps}
                  size='small'
                  style={fieldStyle}
                  disabled={columnIdx === columnsInfos.length - 1 || undefined}
                  min={minWidthOf(columnInfos)}
                  max={
                    columnIdx === columnsInfos.length - 1
                      ? undefined
                      : maxWidthFor(
                          columnTwelfths,
                          columnIdx,
                          GRID,
                          minWidthOf(columnsInfos[columnsInfos.length - 1])
                        )
                  }
                  value={columnTwelfths[columnIdx]}
                  suffix={`/ ${GRID}`}
                  onChange={(v) => v && setColumnWidth(columnIdx, v)}
                />
              </div>

              {columnInfos.drawMode ? (
                <span style={{ ...mutedLabelStyle, fontStyle: 'italic' }}>
                  Mode dessin : lignes indisponibles
                </span>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={labelStyle}>Lignes</span>
                    <InputNumber
                      {...integerInputProps}
                      size='small'
                      style={fieldStyle}
                      // undefined et non false : false l'emporterait sur le
                      // verrouillage du bloc (ConfigProvider du panel).
                      disabled={!canSplit || undefined}
                      min={1}
                      max={MAX_ROWS}
                      value={Math.max(columnInfos.rows.length, 1)}
                      onChange={(v) => v && setRowCount(columnInfos, v)}
                    />
                  </div>

                  {/* Colonne simple : pas encore de ligne, mais une « Ligne 1 »
                      virtuelle permet déjà de la découper en sous-colonnes sans
                      passer par 2 lignes. Même rendu et clé par rang que les
                      vraies lignes : le champ cliqué reste le même nœud DOM
                      quand la ligne se crée. Sinon antd, qui applique la valeur
                      au mousedown, le démonte avant que le mousedown arrive à
                      document, et useOutsideAlerter prend ce clic sur un nœud
                      détaché pour un clic extérieur : tout se désélectionnait. */}
                  {(columnInfos.rows.length
                    ? columnInfos.rows
                    : [{ id: null, columnsCount: 1, columns: [] }]
                  ).map((row, rowIdx) => {
                    const parentTwelfths = columnTwelfths[columnIdx]
                    const rowTwelfths = rowPageTwelfths(row.columns, parentTwelfths)
                    // La cellule cliquée est une sous-colonne : c'est la ligne
                    // qui la porte qu'on éclaire, seul bloc de ce panel qui lui
                    // corresponde.
                    const focusedSub = row.columns.find((sub) => sub.id === focusedCellId)
                    return (
                      <div key={rowIdx} style={groupStyle}>
                        {focusedSub && focusOverlay(focusedSub.id)}
                        <span style={mutedLabelStyle}>Ligne {rowIdx + 1}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={labelStyle}>Colonnes</span>
                          <InputNumber
                            {...integerInputProps}
                            size='small'
                            style={fieldStyle}
                            disabled={!canSplit || undefined}
                            min={1}
                            // Pas de plafond à 4 ici (réservé aux colonnes
                            // principales) : seule limite, 1/12 de page par
                            // sous-colonne.
                            max={parentTwelfths}
                            value={row.columnsCount}
                            onChange={(v) => {
                              if (!v) return
                              if (row.id) return setRowColumnCount(row, v, parentTwelfths)
                              if (v > 1)
                                splitColumnIntoSubColumns({
                                  columnId: columnInfos.id,
                                  columnsSizes: evenTwelfths(v, parentTwelfths).map(
                                    (t) => t / parentTwelfths
                                  ),
                                })
                            }}
                          />
                        </div>
                        {row.columnsCount > 1 && (
                          <WidthFields
                            twelfths={rowTwelfths}
                            total={parentTwelfths}
                            onChange={(index, value) =>
                              setRowColumnWidth(row.id, rowTwelfths, index, value, parentTwelfths)
                            }
                          />
                        )}
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          ),
        }))}
      />
    </div>
  )
}

export default AdvancedLayoutSettings
